import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Link } from "react-router-dom";
import { Edit, Trash2 } from "react-feather";
import Table from "../../core/pagination/datatable";
import CommonFooter from "../../core/common/footer/commonFooter";
import TooltipIcons from "../../core/common/tooltip-content/tooltipIcons";
import RefreshIcon from "../../core/common/tooltip-content/refresh";
import CollapesIcon from "../../core/common/tooltip-content/collapes";
import CommonDeleteModal from "../../core/common/modal/commonDeleteModal";
import { api } from "../../api/client";
import { toast } from "react-toastify";
import {
  FileText,
  MinusCircle,
  PlusCircle,
} from "feather-icons-react/build/IconComponents";
import {
  createStockAdjustment,
  getAllStockAdjustments,
  updateStockAdjustment,
  deleteStockAdjustment,
  getStockAdjustmentById,
} from "../../api/stockAdjustmentApi";
import { getAllWarehouses, getWarehouseById } from "../../api/warehouseApi";
import { getAllStores } from "../../api/storeApi";

/* --------------------------- S3 URL helpers --------------------------- */
const normalizeKey = (s) => {
  if (!s) return "";
  let v = String(s).trim();
  v = v.replace(/^\/+/, "");
  v = v.replace(/^catelog\//i, "catalog/"); // legacy typo -> catalog
  if (!/^catalog\//i.test(v)) {
    v = `catalog/${v.split("/").pop()}`;
  }
  return v;
};

const toPublicUrl = (v) => {
  if (!v) return "";
  if (/^https?:\/\//i.test(v) || /^data:/i.test(v)) return v; // already full
  const key = normalizeKey(v);
  const base = (
    process.env.REACT_APP_API_IMAGE_BASE_URL ||
    "https://hamilton-shop-images.s3.ap-south-1.amazonaws.com"
  ).replace(/\/+$/, "");
  return `${base}/${key}`;
};

// Accept string | string[] | { url|key|src|image|path|file }
const resolveAnyImage = (img) => {
  if (!img) return "";
  if (Array.isArray(img)) return img.length ? resolveAnyImage(img[0]) : "";
  if (typeof img === "object") {
    const candidate =
      img.url || img.key || img.src || img.image || img.path || img.file;
    return resolveAnyImage(candidate);
  }
  return toPublicUrl(String(img));
};
/* --------------------------------------------------------------------- */

const StockAdjustment = () => {
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [storeOptions, setStoreOptions] = useState([]);
  const [responsiblePersonOptions, setResponsiblePersonOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  const [warehouse, setWarehouse] = useState(null);
  const [store, setStore] = useState(null);
  const [pricingStockId, setPricingStockId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [person, setPerson] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [adjustmentType, setAdjustmentType] = useState(null);
  const [notes, setNotes] = useState("");

  const [adjustmentList, setAdjustmentList] = useState([]);
  const [editData, setEditData] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedNotes, setSelectedNotes] = useState("");
  const [variantId, setVariantId] = useState(null);

  useEffect(() => {
    (async () => {
      const [warehouses, stores] = await Promise.all([
        getAllWarehouses(),
        getAllStores(),
      ]);
      setWarehouseOptions(
        warehouses.map((w) => ({ label: w.warehouse, value: w.id })),
      );
      setStoreOptions(
        stores.map((s) => ({ label: s.CustomerName, value: s.id })),
      );
    })();
  }, []);

  // Fetch products for the selected store and normalize image URLs for the select options
  const fetchProductsByStore = async (storeId) => {
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}api/products/by-store/${storeId}`,
      );

      // Make sure each option carries a full image URL
      const opts = (Array.isArray(res.data) ? res.data : []).map((p) => ({
        ...p,
        image: resolveAnyImage(p.image || p.images),
      }));
      setProductOptions(opts);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to load products")}`);
    }
  };

  const handleCreateAdjustment = async () => {
    if (
      !warehouse ||
      !store ||
      !selectedProduct ||
      !person ||
      !adjustmentType
    ) {
      toast.error(`❌ ${window.translate("Please fill all required fields")}`);
      return;
    }

    const selectedPerson = responsiblePersonOptions.find(
      (p) => p.value === person,
    );

    const productId =
      selectedProduct?.productId ||
      selectedProduct?._id ||
      selectedProduct?.product?._id ||
      selectedProduct?.value;

    // const payload = {
    //   warehouse,
    //   store,
    //   pricingStock: pricingStockId || null,
    //   productId,
    //   personName: {
    //     en: selectedPerson?.label || "",
    //     ar: selectedPerson?.label || "",
    //   },
    //   personImage: selectedPerson?.image,
    //   productImage: resolveAnyImage(selectedProduct?.image), // ensure full
    //   productLabel: selectedProduct?.label,
    //   sku: selectedProduct?.sku,
    //   category: selectedProduct?.category,
    //   quantity,
    //   adjustmentType,
    //   notes,
    // };

    const payload = {
      warehouse,
      store,
      productId,
      variantId: variantId || null,
      pricingStock: pricingStockId || null,
      personName: {
        en: selectedPerson?.label || "",
        ar: selectedPerson?.label || "",
      },
      personImage: selectedPerson?.image,
      quantity,
      adjustmentType,
      notes,
    };

    try {
      await createStockAdjustment(payload);
      toast.success(` ${window.translate("Adjustment added successfully")}`);
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to create adjustment")}`);
    }
  };

  // Load list + normalize images
  useEffect(() => {
    (async () => {
      const [warehouses, stores, adjustmentsRes] = await Promise.all([
        getAllWarehouses(),
        getAllStores(),
        getAllStockAdjustments(),
      ]);

      setWarehouseOptions(
        warehouses.map((w) => ({ label: w.warehouse, value: w.id })),
      );
      setStoreOptions(
        stores.map((s) => ({ label: s.CustomerName, value: s.id })),
      );

      const lang = localStorage.getItem("lang") === "ar" ? "ar" : "en";
      const rows = Array.isArray(adjustmentsRes?.data)
        ? adjustmentsRes.data
        : Array.isArray(adjustmentsRes)
          ? adjustmentsRes
          : [];

      const formatted = rows.map((item) => ({
        ...item,
        key: item._id,
        warehouseName:
          item.warehouse?.warehouseName?.[lang] ||
          item.warehouse?.warehouseName?.en ||
          "-",
        storeName:
          item.store?.storeName?.[lang] || item.store?.storeName?.en || "-",
        productLabel:
          item.productName ||
          item.product?.productName?.[lang] ||
          item.product?.productName?.en ||
          item.productLabel ||
          "-",
        productImage: resolveAnyImage(
          item.productImage || item.product?.images || "",
        ),
      }));

      setAdjustmentList(formatted);
    })();
  }, []);

  const handleDeleteAdjustment = async (id) => {
    try {
      await deleteStockAdjustment(id);
      toast.success(` ${window.translate("Adjustment deleted successfully")}`);
      const updatedList = adjustmentList.filter((adj) => adj._id !== id);
      setAdjustmentList(updatedList);
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Failed to delete adjustment")}`);
    }
  };

  const columns = [
    {
      title: window.translate("Warehouse"),
      dataIndex: "warehouseName",
      sorter: (a, b) => a.warehouseName.localeCompare(b.warehouseName),
    },
    {
      title: window.translate("Stores"),
      dataIndex: "storeName",
      sorter: (a, b) => a.storeName.localeCompare(b.storeName),
    },
    {
      title: window.translate("Product"),
      dataIndex: "productLabel",
      render: (text, record) => (
        <span className="userimgname">
          <Link to="#" className="product-img">
            <img
              alt="img"
              src={
                resolveAnyImage(record.productImage) ||
                "/assets/img/products/default.png"
              }
              style={{
                width: 40,
                height: 40,
                objectFit: "cover",
                borderRadius: "5px",
              }}
            />
          </Link>
          <Link to="#">{record.productLabel}</Link>
        </span>
      ),
      sorter: (a, b) => a.productLabel.localeCompare(b.productLabel),
    },
    {
      title: window.translate("Date"),
      dataIndex: "createdAt",
      render: (text) => new Date(text).toLocaleDateString(),
      sorter: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
    },
    {
      title: window.translate("Person"),
      dataIndex: "personName",
      render: (text, record) => (
        <span className="userimgname">
          <Link to="#">{record.personName?.en || "-"}</Link>
        </span>
      ),
      sorter: (a, b) =>
        (a.personName?.en || "").localeCompare(b.personName?.en || ""),
    },
    {
      title: window.translate("Qty"),
      dataIndex: "quantity",
      render: (text) => text,
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: window.translate("Action"),
      dataIndex: "action",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            <Link
              className="me-2 p-2"
              to="#"
              onClick={() => handleViewNotes(record._id)}
            >
              <FileText className="feather-file-text" />
            </Link>
            <Link
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-units"
              onClick={() => handleEditClick(record)}
            >
              <Edit className="feather-edit" />
            </Link>
            <Link
              className="confirm-text p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#delete-modal"
              onClick={() => setSelectedId(record._id)}
            >
              <Trash2 className="feather-trash-2" />
            </Link>
          </div>
        </div>
      ),
    },
  ];

  const handleEditClick = async (item) => {
    setEditData(item);

    setWarehouse(item.warehouse?._id);
    setStore(item.store?._id);

    setPricingStockId(item.pricingStock?._id || null);
    setVariantId(item.variant?._id || null);

    setQuantity(item.quantity);
    setAdjustmentType(item.adjustmentType);
    setNotes(item.notes);

    setPerson(item.personName?.en);

    const wh = await getWarehouseById(item.warehouse?._id);
    if (wh?.contactPerson?.en) {
      setResponsiblePersonOptions([
        {
          label: wh.contactPerson.en,
          value: wh.contactPerson.en,
          image: "/assets/img/users/avatar-02.jpg",
        },
      ]);
    }
  };

  const handleUpdateAdjustment = async () => {
    if (
      !editData ||
      !warehouse ||
      !store ||
      (!pricingStockId && !variantId) ||
      !person
    ) {
      toast.error(`❌ ${window.translate("Please fill all required fields")}`);
      return;
    }

    const selectedPerson = responsiblePersonOptions.find(
      (p) => p.value === person,
    );

   const payload = {
  warehouse,
  store,
  pricingStock: pricingStockId || null,
  variant: variantId || null,
  quantity,
  adjustmentType,
  notes,
  personName: {
    en: selectedPerson?.label || "",
    ar: selectedPerson?.label || "",
  },
  personImage: selectedPerson?.image,
};

    try {
      await updateStockAdjustment(editData._id, payload);
      toast.success(` ${window.translate("Stock adjustment updated")}`);
      document.getElementById("close-add-stock-btn")?.click();
      setTimeout(() => {
        window.location.reload();
      }, 800);
    } catch (err) {
      toast.error(`❌ ${window.translate("Update failed")}`);
    }
  };

  const handleViewNotes = async (id) => {
    const t = (s) => (window?.translate ? window.translate(s) : s);
    try {
      const notesText = await getStockAdjustmentById(id);
      setSelectedNotes(notesText || "");
      const el = document.getElementById("view-notes");
      if (el) {
        const modal = window.bootstrap
          ? window.bootstrap.Modal.getOrCreateInstance(el)
          : null;
        modal ? modal.show() : el.classList.add("show");
      }
    } catch (err) {
      console.error("Failed to load notes:", err);
      toast.error(`❌ ${t("Failed to load notes")}`);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>{window.translate("Stock Adjustment")}</h4>
                <h6>{window.translate("Manage your stock adjustment")}</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <TooltipIcons />
              <RefreshIcon />
              <CollapesIcon />
            </ul>
            <div className="page-btn">
              <Link
                to="#"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
              >
                <i className="ti ti-circle-plus me-2 ms-2"></i>
                {window.translate("Add Adjustment")}
              </Link>
            </div>
          </div>

          <div className="card table-list-card manage-stock">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <div className="search-set"></div>
              <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-2">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("Warehouse")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Lavish Warehouse
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Quaint Warehouse
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Traditional Warehouse
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        Cool Warehouse
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white btn-md d-inline-flex align-items-center d-none"
                    data-bs-toggle="dropdown"
                  >
                    {window.translate("sortLast7Days")}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("recentlyAdded")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("ascending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("descending")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("lastMonth")}
                      </Link>
                    </li>
                    <li>
                      <Link to="#" className="dropdown-item rounded-1">
                        {window.translate("last7Days")}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="card-body">
              <div className="custom-datatable-filter table-responsive">
                {adjustmentList.length > 0 ? (
                  <Table columns={columns} dataSource={adjustmentList} />
                ) : (
                  <p className="text-muted text-center">
                    {window.translate("No adjustments found.")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        <CommonFooter />
      </div>

      {/* Add Adjustment */}
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>{window.translate("Add Adjustment")}</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Warehouse")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      options={warehouseOptions}
                      onChange={async (e) => {
                        setWarehouse(e.value);
                        const wh = await getWarehouseById(e.value);
                        if (wh.contactPerson) {
                          const formatted = [
                            {
                              label: wh.contactPerson.en,
                              value: wh.contactPerson.en,
                              image: "/assets/img/users/avatar-02.jpg",
                            },
                          ];
                          setResponsiblePersonOptions(formatted);
                          setPerson(wh.contactPerson.en);
                        }
                      }}
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Store")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      options={storeOptions}
                      onChange={async (e) => {
                        setStore(e.value);
                        await fetchProductsByStore(e.value);
                      }}
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Product")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      options={productOptions}
                      getOptionLabel={(e) => `${e.label} (${e.sku})`}
                      onChange={(e) => {
                        setSelectedProduct({
                          ...e,
                          image: resolveAnyImage(e.image),
                        });

                        if (e.productType === "variable") {
                          setPricingStockId(null);
                          setVariantId(e.value); // this must be variant _id
                        } else {
                          setVariantId(null);
                          setPricingStockId(e.value); // this is pricingStock _id
                        }
                      }}
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Adjustment Type")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      options={[
                        { label: "Addition", value: "addition" },
                        { label: "Subtraction", value: "subtraction" },
                      ]}
                      onChange={(e) => setAdjustmentType(e.value)}
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Responsible Person")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <Select
                      options={responsiblePersonOptions}
                      value={responsiblePersonOptions.find(
                        (p) => p.value === person,
                      )}
                      onChange={(e) => setPerson(e.value)}
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Quantity")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <div className="d-flex align-items-center">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          setQuantity((q) => Math.max(1, q - 1));
                        }}
                      >
                        <i className="feather-minus-circle" />
                      </button>
                      <input
                        type="number"
                        className="form-control mx-2"
                        value={quantity}
                        onChange={(e) =>
                          setQuantity(parseInt(e.target.value) || 1)
                        }
                        style={{ width: "80px" }}
                      />
                      <button
                        className="btn btn-light btn-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          setQuantity((q) => q + 1);
                        }}
                      >
                        <i className="feather-plus-circle" />
                      </button>
                    </div>
                  </div>

                  <div className="col-lg-12 mb-3">
                    <label className="form-label">
                      {window.translate("Notes")}
                      <span className="text-danger ms-1">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  data-bs-dismiss="modal"
                >
                  {window.translate("Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateAdjustment}
                  data-bs-dismiss="modal"
                >
                  {window.translate("Create Adjustment")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Add Adjustment */}

      {/* Edit Adjustment */}
      <div className="modal fade" id="edit-units">
        <div className="modal-dialog modal-dialog-centered stock-adjust-modal">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>{window.translate("Edit Adjustment")}</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <form>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Warehouse")}
                    </label>
                    <Select
                      options={warehouseOptions}
                      value={warehouseOptions.find(
                        (opt) => opt.value === warehouse,
                      )}
                      onChange={(e) => setWarehouse(e.value)}
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Store")}
                    </label>
                    <Select
                      options={storeOptions}
                      value={storeOptions.find((opt) => opt.value === store)}
                      onChange={(e) => setStore(e.value)}
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Responsible Person")}
                    </label>
                    <Select
                      options={responsiblePersonOptions}
                      value={responsiblePersonOptions.find(
                        (opt) => opt.value === person,
                      )}
                      onChange={(e) => setPerson(e.value)}
                    />
                  </div>
                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      {window.translate("Type")}
                    </label>
                    <Select
                      options={[
                        { label: "Addition", value: "addition" },
                        { label: "Subtraction", value: "subtraction" },
                      ]}
                      value={{ label: adjustmentType, value: adjustmentType }}
                      onChange={(e) => setAdjustmentType(e.value)}
                    />
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">
                      {window.translate("Quantity")}
                    </label>
                    <div className="product-quantity border-0 bg-gray-transparent">
                      <span
                        className="quantity-btn"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      >
                        <MinusCircle />
                      </span>
                      <input
                        type="text"
                        className="quntity-input bg-transparent"
                        value={quantity}
                        readOnly
                      />
                      <span
                        className="quantity-btn"
                        onClick={() => setQuantity((q) => q + 1)}
                      >
                        <PlusCircle className="plus-circle" />
                      </span>
                    </div>
                  </div>
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">
                      {window.translate("Notes")}
                    </label>
                    <textarea
                      className="form-control"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary me-2"
                  data-bs-dismiss="modal"
                >
                  {window.translate("Cancel")}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateAdjustment}
                >
                  {window.translate("Save Changes")}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Adjustment */}

      {/* View Notes */}
      <div className="modal fade" id="view-notes">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <div className="page-title">
                <h4>{window.translate("Notes")}</h4>
              </div>
              <button
                type="button"
                className="close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              {selectedNotes?.length > 0 ? (
                <p className="text-muted">{selectedNotes}</p>
              ) : (
                <p className="text-muted">
                  {window.translate("No notes available.")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* /View Notes */}

      <CommonDeleteModal
        title={window.translate("Delete Stock")}
        message={window.translate(
          "Are you sure you want to delete this stock entry?",
        )}
        onConfirm={() => handleDeleteAdjustment(selectedId)}
      />
    </>
  );
};

export default StockAdjustment;
